import React from 'react';
import {
    Button,
    TextInput,
    Textarea,
    Box,
    Alert,
    Loader,
    ActionIcon,
    Checkbox,
    Text,
    Paper,
    Select,
    Tooltip,
    Badge,
    Group,
    Anchor,
} from '@mantine/core';
import {
    IconX,
    IconSearch,
    IconEye,
    IconUpload,
    IconCalendar,
    IconAlertTriangle,
} from '@tabler/icons-react';
import { detectFieldType } from '../../../utils/fieldTypeDetection';
import { createEntityRef, getEntityValueFromARef, normalizeKey, type EntityRef } from '../../../types';
import { isARef } from '../../../utils/entityPropertyUtils';
import {
    formatTier3AttributeSizeEstimate,
    getBindingViolationMessage,
    getTargetEntitlementItemForAttribute,
    getTier2AttributeBindingForAttribute,
    tier3AttributeEncrypted,
    tier3ItemDecryptAllowed,
    isReferenceToSchemaAttribute,
} from '../../../types/EntityEntitlements';
import { extractReferenceDisplayName, extractReferenceId } from '../../../utils/referenceUtils';
import { formatPhoneDisplayIfApplicable } from '../../../utils/phoneUtils';
import EntityBrowserDialog from '../../dialogs/EntityBrowserDialog';
import { SetSelector } from '../../common/SetSelector';
import { FunctionCatalogSelector } from '../../common/FunctionCatalogSelector';
import { GenericReferenceField } from '../../common/GenericReferenceField';
import { MultiReferenceEntityBadge } from '../../common/MultiReferenceEntityBadge';
import { ShadcnContentEditor } from '../../common/ShadcnContentEditor';
import { EntityTypeSelector } from '../../common/EntityTypeSelector';
import {
    tryRenderPolicyRuleField,
    renderPossessiveTargetAttributeField,
    renderPossessiveActorsAttributesField,
    renderTypeField,
    renderCompletedApprovalEmailField,
    renderPossessiveScopeField,
} from '../fieldRenderers';
import type { CompletedApprovalEmailFieldProps, TypeFieldProps } from '../fieldRenderers';
import { getFieldTooltipTitle } from '../entityFieldDebugUtils';
import { stringifyDebugNotes, debugLog } from '../../../utils/debugLog';
import { BodyHtmlEditor } from '../BodyHtmlEditor';
import { ShadcnBinaryAttributeField } from '../ShadcnBinaryAttributeField';
import { EncryptedAttributeField } from '../EncryptedAttributeField';
import {
    getWhitespaceIssues,
    hasWhitespaceIssues,
    isExpectedMultilineField,
} from '../entityFieldValidationUtils';
import { DeferredLoadPlaceholder, FieldLabelWithRequired } from '../renderDynamicField';
import { ScimAceEditor } from '../../common/ScimAceEditor';
import { SqlEditorWithFullscreen } from '../../common/SqlEditorWithFullscreen';
import type { RenderFieldLocals } from './renderFieldLocals';
import { bindRenderFieldScope, type ContentTypeHelpers } from './bindRenderFieldScope';

export function renderEntityUpsertFieldBody(
    locals: RenderFieldLocals,
    helpers: ContentTypeHelpers,
    attr: Record<string, unknown>
): React.ReactNode {
    const s = bindRenderFieldScope(locals, helpers, attr);
    const {
        wrapWithFieldDebug,
        formData,
        existingEntity,
        entityType,
        entityId,
        isEditMode,
        isViewSurface,
        loading,
        schemaAttributes,
        deferredArrayRefNamesSet,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        onRequestAttributeValue,
        loadingAttribute,
        handleFieldChange,
        handleFieldChangeRef,
        isSetTransition,
        selectedActionWorkflow,
        actionWorkflowError,
        setActionWorkflowBrowserOpen,
        formKeyFromAttrName,
        handleGroupPreviewMembers,
        handleFilterPreview,
        attributeRefBrowserField,
        setAttributeRefBrowserField,
        loadingPossessiveScopeTier2,
        tier2Entitlement,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromTier2,
        possessiveScope,
        isPluginFunction,
        isRequestApprovalType,
        isPolicyRule,
        handleRequestorSetChange,
        handleTargetSetChange,
        targetSetFilterScope,
        relativeTargetAttributeError,
        setRelativeTargetAttributeError,
        policyRuleOnlySingleValueAttributesSelected,
        isContent,
        onContentLinkClick,
        isEmailTemplate,
        isFile,
        isGroup,
        originalData,
        setOriginalData,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        multiValuedRawInputs,
        setMultiValuedRawInputs,
        emailTemplatesList,
        loadingEmailTemplates,
        allEntityTypes,
        entityTypesFromContext,
        disjointedEntityTypes,
        possessiveTargetAttributeKey,
        loadingPossessiveTargetAttributeTier2,
        tier2PossessiveTargetAttributeEntitlement,
        referenceAttributesFromPossessiveTargetAttribute,
        selectedReferenceAttributeInfo,
        actorsAttributeBrowserOpen,
        setActorsAttributeBrowserOpen,
        setPossessiveWipeConfirmPending,
        setPossessiveWipeConfirmOpen,
        attributesFromPossessiveTargetAttributeTier2,
        contentTypesCacheService,
        setError,
        targetEntitlement,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        setMultiRefBrowserField,
        name,
        label,
        value,
        attrRef,
        isStubValueInner,
        isRequired,
        allowedValues,
        bindingRegex,
        isReadOnlyField,
        isFieldDisabled,
        isPossessiveType,
        getBodyContentType,
        isHtmlField,
        isExternalManaged,
    } = s;

        // Tier3 delay-load: non-indexed + hasLengthCount attributes not in fetch show "Not loaded*" + Load; deferred array refs show View / Add items / Remove items.
        // Must run before any custom renderer so layouts that call renderField(attr) (e.g. PolicyRuleFormLayout) respect it.
        if (isStubValueInner && isEditMode && entityId && name) {
            const tier3SizeEstimate = formatTier3AttributeSizeEstimate(name, targetEntitlement);
            const isDeferredArrayRef = deferredArrayRefNamesSet.has(normalizeKey(name));
            if (isDeferredArrayRef) {
                const pendingAdds = deferredArrayPendingAdds[name] ?? [];
                const pendingRemoves = deferredArrayPendingRemoves[name] ?? [];
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text size="sm" fw={500} mb={4}>
                            <FieldLabelWithRequired label={label} required={false} readOnly={isReadOnlyField} />
                        </Text>
                        <Group gap="xs" align="center" wrap="wrap">
                            <Tooltip label="Large reference list (paginated view)">
                                <Text component="span" size="sm" c="dimmed">
                                    Not loaded*{tier3SizeEstimate ? ` (${tier3SizeEstimate})` : ''}
                                </Text>
                            </Tooltip>
                            <Anchor component="button" type="button" size="sm" c="dimmed" onClick={() => setPaginatedArrayRefViewer({ field: name, mode: 'view' })}>
                                View
                            </Anchor>
                            {!isReadOnlyField && (
                                <>
                                    <Anchor component="button" type="button" size="sm" c="dimmed" onClick={() => setDeferredArrayAddField(name)}>
                                        Add items
                                    </Anchor>
                                    <Anchor component="button" type="button" size="sm" c="dimmed" onClick={() => setPaginatedArrayRefViewer({ field: name, mode: 'remove' })}>
                                        Remove items
                                    </Anchor>
                                </>
                            )}
                            {pendingAdds.length > 0 && (
                                <Badge size="sm" variant="light" color="green">{pendingAdds.length} to add</Badge>
                            )}
                            {pendingRemoves.length > 0 && (
                                <Badge size="sm" variant="light" color="red">{pendingRemoves.length} to remove</Badge>
                            )}
                        </Group>
                    </Box>
                ), 'DeferredLoadPlaceholder', isStubValueInner);
            }
            if (onRequestAttributeValue) {
                return wrapWithFieldDebug(name, (
                    <DeferredLoadPlaceholder
                        fieldName={name}
                        fieldLabel={label}
                        readOnly={isReadOnlyField}
                        loading={loadingAttribute === name}
                        onLoad={async () => { const v = await onRequestAttributeValue(name); handleFieldChange(name, v ?? ''); }}
                        tooltipLabel="Sensitive, Large, or File value"
                        sizeEstimate={tier3SizeEstimate}
                    />
                ), 'DeferredLoadPlaceholder', isStubValueInner);
            }
        }

        // SQL fields: use SQL editor (e.g. SqlStatement, SqlStatementExplicit, _query) so delay-loaded values get correct renderer
        if (entityType && name) {
            const fieldType = detectFieldType(name, value, entityType, formData);
            if (fieldType.isSqlField) {
                const sqlValue = typeof value === 'string' ? value : String(value ?? '');
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <SqlEditorWithFullscreen
                            labelRow={(
                                <Text size="sm" fw={500} component="span">
                                    <FieldLabelWithRequired label={label} required={isRequired} readOnly={isFieldDisabled} />
                                </Text>
                            )}
                            value={sqlValue}
                            onChange={(str) => handleFieldChange(name, str)}
                            readOnly={isFieldDisabled}
                            fieldName={String(name ?? 'sql')}
                            modalTitle={typeof label === 'string' ? label : 'SQL'}
                        />
                    </Box>
                ), 'AceEditor-sql', isStubValueInner);
            }
        }

        // Special handling for SetTransition Direction field
        if (isSetTransition && isARef(attrRef, 'Direction')) {
            return wrapWithFieldDebug(name, (
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <Select
                        key={name}
                        label={label}
                        required={isRequired}
                        value={value != null && value !== '' ? String(value) : ''}
                        onChange={(v) => handleFieldChange(name, v ?? '')}
                        disabled={isFieldDisabled}
                        data={[{ value: 'In', label: 'In' }, { value: 'Out', label: 'Out' }]}
                        mb="md"
                    />
                </Tooltip>
            ), 'SetTransitionDirection');
        }

        // Special handling for SetTransition ActionWorkflow field (entity selector)
        if (isSetTransition && isARef(attrRef, 'ActionWorkflow')) {
            const handleSelectActionWorkflow = () => {
                setActionWorkflowBrowserOpen(true);
            };

            return wrapWithFieldDebug(name, (
                <TextInput
                    key={name}
                    label={label}
                    value={String(selectedActionWorkflow?.DisplayName || selectedActionWorkflow?.Id || (value != null && value !== '' ? value : '') || '')}
                    placeholder="No workflow selected"
                    error={actionWorkflowError}
                    description={actionWorkflowError}
                    readOnly
                    rightSection={
                        <Button
                            variant="subtle"
                            size="xs"
                            onClick={handleSelectActionWorkflow}
                            disabled={isFieldDisabled}
                        >
                            Select Workflow
                        </Button>
                    }
                    rightSectionWidth={140}
                    mb="md"
                />
            ), 'ActionWorkflow');
        }

        // Special handling for SetTransition TargetSet field (Set selector)
        if (isSetTransition && isARef(attrRef, 'TargetSet')) {
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <SetSelector
                        value={value != null && value !== '' ? String(value) : null}
                        onChange={(setId) => handleFieldChange(name, setId || '')}
                        label={label}
                        required={isRequired}
                        disabled={isFieldDisabled}
                        allowClear={true}
                    />
                </Box>
            ), 'TargetSet');
        }

        // Special handling for Group FilterScope field (entity type selector). Store under normalized key so formData has FilterScope: "Student" not "$.FilterScope".
        if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'FilterScope')) {
            const filterScopeKey = formKeyFromAttrName(name) || name;
            const rawVal = getEntityValueFromARef(name, formData);
            const filterScopeValue = (rawVal != null && rawVal !== '' && typeof rawVal === 'string' && !rawVal.startsWith('$.'))
                ? rawVal
                : null;
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <EntityTypeSelector
                        value={filterScopeValue}
                        onChange={(entityTypeValue) => handleFieldChange(filterScopeKey, entityTypeValue || '')}
                        label={label}
                        required={isRequired}
                        disabled={isFieldDisabled}
                        allowClear={true}
                        helperText="Select the entity type that this group's filter applies to"
                    />
                </Box>
            ), 'FilterScope');
        }

        // Special handling for Set FilterScope field (entity type selector). Store under normalized key so formData has FilterScope: "Student" not "$.FilterScope".
        if (entityType?.toLowerCase() === 'set' && isARef(attrRef, 'FilterScope')) {
            const filterScopeKey = formKeyFromAttrName(name) || name;
            const rawVal = getEntityValueFromARef(name, formData);
            const filterScopeValue = (rawVal != null && rawVal !== '' && typeof rawVal === 'string' && !rawVal.startsWith('$.'))
                ? rawVal
                : null;
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <EntityTypeSelector
                        value={filterScopeValue}
                        onChange={(entityTypeValue) => handleFieldChange(filterScopeKey, entityTypeValue || '')}
                        label={label}
                        required={isRequired}
                        disabled={isFieldDisabled}
                        allowClear={true}
                        helperText="Entity type that set members belong to (filter applies to this type)"
                    />
                </Box>
            ), 'FilterScope');
        }

        // Special handling for Group Filter field: Textarea + Preview Members (only for enumerated managed type)
        if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'Filter')) {
            const filterScopeVal = (getEntityValueFromARef('FilterScope', formData) ?? '').toString().trim();
            const managedTypeVal = (getEntityValueFromARef('ManagedType', formData) ?? '').toString().toLowerCase();
            const isEnumeratedManaged = managedTypeVal === 'enumerated';
            const filterDisplay = typeof value === 'string' ? value : String(value ?? '');
            return wrapWithFieldDebug(name, (
                <Textarea
                    key={name}
                    label={label}
                    placeholder="SCIM filter expression (e.g. active eq true)"
                    value={filterDisplay}
                    onChange={(e) => handleFieldChange(name, e.target.value)}
                    onBlur={(e) => {
                        const v = e.currentTarget.value.trim();
                        if (v !== filterDisplay) handleFieldChange(name, v);
                    }}
                    disabled={isFieldDisabled}
                    minRows={2}
                    mb="md"
                    rightSection={
                        <Button
                            variant="outline"
                            size="xs"
                            leftSection={<IconEye size={14} />}
                            onClick={handleGroupPreviewMembers}
                            disabled={isFieldDisabled || !filterScopeVal || !isEnumeratedManaged}
                            style={{ whiteSpace: 'nowrap' }}
                            title={!isEnumeratedManaged ? 'Preview is only available for enumerated managed groups' : undefined}
                        >
                            Preview Members
                        </Button>
                    }
                    rightSectionWidth={170}
                    rightSectionPointerEvents="auto"
                />
            ), 'Filter');
        }

        // Special handling for Set Filter field: Textarea + Preview button (opens table of results when FilterScope is set)
        if (entityType?.toLowerCase() === 'set' && isARef(attrRef, 'Filter')) {
            const filterScopeVal = (getEntityValueFromARef('FilterScope', formData) ?? '').toString().trim();
            const setFilterDisplay = typeof value === 'string' ? value : String(value ?? '');
            return wrapWithFieldDebug(name, (
                <Textarea
                    key={name}
                    label={label}
                    placeholder="SCIM filter expression (e.g. active eq true)"
                    value={setFilterDisplay}
                    onChange={(e) => handleFieldChange(name, e.target.value)}
                    onBlur={(e) => {
                        const v = e.currentTarget.value.trim();
                        if (v !== setFilterDisplay) handleFieldChange(name, v);
                    }}
                    disabled={isFieldDisabled}
                    minRows={2}
                    mb="md"
                    rightSection={
                        <Button
                            variant="outline"
                            size="xs"
                            leftSection={<IconEye size={14} />}
                            onClick={handleFilterPreview}
                            disabled={isFieldDisabled || !filterScopeVal}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            Preview
                        </Button>
                    }
                    rightSectionWidth={100}
                    rightSectionPointerEvents="auto"
                />
            ), 'Filter');
        }

        // Special handling for Group Scope field (Global, Local, or Universal) - required
        if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'Scope')) {
            // Local can only be selected if it was already set (existing entity with Local scope)
            const currentScope = value || '';
            const hasLocalScope = currentScope === 'Local';
            const showLocalOption = hasLocalScope; // Only show Local if it's already set
            const displayValue = (value != null && value !== '' ? String(value) : '') || 'Universal'; // Default to Universal if empty

            // NOTE: Do NOT manually add "*" to the label - FormControl's required={true} prop automatically adds it
            // Adding it manually would result in "**" (double asterisks)
            return wrapWithFieldDebug(name, (
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <Select
                        key={name}
                        label={label}
                        required={true}
                        value={displayValue}
                        onChange={(v) => handleFieldChange(name, v ?? '')}
                        disabled={isFieldDisabled}
                        data={[
                            { value: 'Global', label: 'Global' },
                            ...(showLocalOption ? [{ value: 'Local', label: 'Local' }] : []),
                            { value: 'Universal', label: 'Universal' }
                        ]}
                        mb="md"
                    />
                </Tooltip>
            ), 'Scope');
        }

        // Special handling for Group Type field (Security or Distribution) - required
        if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'Type')) {
            // NOTE: Do NOT manually add "*" to the label - FormControl's required={true} prop automatically adds it
            // Adding it manually would result in "**" (double asterisks)
            return wrapWithFieldDebug(name, (
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <Select
                        key={name}
                        label={label}
                        required={true}
                        value={value != null && value !== '' ? String(value) : ''}
                        onChange={(v) => handleFieldChange(name, v ?? '')}
                        disabled={isFieldDisabled}
                        data={[
                            { value: 'Security', label: 'Security' },
                            { value: 'Distribution', label: 'Distribution' }
                        ]}
                        mb="md"
                    />
                </Tooltip>
            ), 'Type');
        }

        // Tier2-driven: reference fields that point to SchemaAttribute (IsReferenceTo) show attribute list, not entity picker
        // Only handle PossessiveTargetAttribute here. PossessiveActorsAttribute / PossessiveActorsAttributes are reference-to-SchemaAttribute too
        // but must use their own renderers with options from tier2 of the *target* entity type (e.g. School), not this block (which would use PluginFunction tier2).
        if (attr.isReference && isReferenceToSchemaAttribute(attr)) {
            const fieldName = (attr.name || attr.id || attr.value || '').toString();
            // Match by name or id only (not value). Edit mode: schema may use id as primary.
            const isPossessiveTargetAttr = isARef(attrRef, 'PossessiveTargetAttribute')
                || (attr.id != null && attr.id !== '' && isARef(createEntityRef(String(attr.id)), 'PossessiveTargetAttribute'));
            if (!isPossessiveTargetAttr) {
                // Fall through so PossessiveActorsAttribute / PossessiveActorsAttributes hit their special cases below (tier2 from target type)
            } else if (isRequestApprovalType && !isPossessiveType) {
                // RequestApprovalType with Type Generic: show disabled empty field (no selector or magnifying glass)
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={false} readOnly={true} />
                        </Text>
                        <TextInput disabled value="" placeholder="—" />
                    </Box>
                ), 'PossessiveTargetAttribute');
            } else {
                const dependentFieldToClear = isPossessiveTargetAttr ? 'PossessiveActorsAttribute' : undefined;
                // For PSGroup (RequestApprovalType/PluginFunction) PossessiveTargetAttribute: options come from tier2 of the selected PossessiveScope entity type
                const usePossessiveScopeTier2 = (isRequestApprovalType || isPluginFunction) && isPossessiveTargetAttr;
                const loadingRefAttrs = usePossessiveScopeTier2 ? loadingPossessiveScopeTier2 : (tier2Entitlement == null);
                const refAttrs = usePossessiveScopeTier2 ? referenceAttributesFromPossessiveScope : referenceAttributesFromTier2;
                return wrapWithFieldDebug(name, renderPossessiveTargetAttributeField({
                    name: fieldName,
                    label,
                    value: value ?? formData[fieldName],
                    isRequired,
                    loading: isFieldDisabled || (isRequestApprovalType && !isPossessiveType),
                    loadingAllReferenceAttributes: loadingRefAttrs,
                    allReferenceAttributes: refAttrs,
                    formData,
                    handleFieldChange,
                    targetAttributeBrowserOpen: attributeRefBrowserField === fieldName,
                    setTargetAttributeBrowserOpen: (open) => setAttributeRefBrowserField(open ? fieldName : null),
                    dependentFieldToClear,
                    helperTextWhenEmpty: usePossessiveScopeTier2 && !possessiveScope ? 'Select PossessiveScope first' : undefined
                }), 'PossessiveTargetAttribute');
            }
        }

        if (isPolicyRule) {
            const policyRuleField = tryRenderPolicyRuleField(attrRef, {
                attr,
                attrRef,
                name,
                label,
                value,
                isRequired,
                isFieldDisabled,
                formData,
                handleFieldChange,
                handleRequestorSetChange,
                handleTargetSetChange,
                targetSetFilterScope,
                relativeTargetAttributeError,
                setRelativeTargetAttributeError,
                wrapWithFieldDebug,
            });
            if (policyRuleField != null) {
                return policyRuleField;
            }
        }

        // PluginFunction: CatalogId field (function catalog selector)
        if (isPluginFunction && isARef(attrRef, 'CatalogId')) {
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <FunctionCatalogSelector
                        value={value != null && value !== '' ? String(value) : null}
                        onChange={(catalogId) => handleFieldChange(name, catalogId || '')}
                        label={label}
                        required={isRequired}
                        disabled={isFieldDisabled}
                        allowClear={true}
                        helperText="Select a function catalog item to associate with this plugin function"
                    />
                </Box>
            ), 'CatalogId');
        }

        // Special handling for PSGroup (RequestApprovalType) PossessiveActorsAttribute (reference attribute single select)
        // Options come ONLY from tier2 for the entity type that PossessiveTargetAttribute references. When Type is Generic, disabled and empty.
        if (isRequestApprovalType && isARef(attrRef, 'PossessiveActorsAttribute')) {
            if (!isPossessiveType) {
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={false} readOnly={true} />
                        </Text>
                        <TextInput disabled value="" placeholder="—" />
                    </Box>
                ), 'PossessiveActorsAttribute');
            }
            const targetValue = getEntityValueFromARef('PossessiveTargetAttribute', formData);
            const targetString = typeof targetValue === 'string' ? targetValue : (targetValue ? String(targetValue) : '');
            const targetAttrName = targetString ? targetString.trim() : '';

            const hasTarget = !!possessiveTargetAttributeKey;
            const actorsOptions = hasTarget && (loadingPossessiveTargetAttributeTier2 || tier2PossessiveTargetAttributeEntitlement != null)
                ? referenceAttributesFromPossessiveTargetAttribute
                : [];
            const actorsLoading = hasTarget && loadingPossessiveTargetAttributeTier2;

            // Get current value for display in placeholder
            const currentValue = Array.isArray(value) ? (value.length > 0 ? value[0] : '') : (value || '');

            // Try to find DisplayName from already loaded attributes if available
            let currentValueDisplay = '';
            if (currentValue && actorsOptions.length > 0) {
                const matchingAttr = actorsOptions.find((opt) => {
                    const attrId = (opt.id || opt.value || '').toString().trim();
                    const cv = (currentValue ?? '').toString().trim();
                    return attrId === cv || attrId.toLowerCase() === cv.toLowerCase();
                });
                currentValueDisplay = matchingAttr ? (matchingAttr.label || matchingAttr.name || currentValue) : currentValue;
            } else if (currentValue) {
                currentValueDisplay = String(currentValue);
            }

            // Don't render the selector at all if there's no target attribute selected
            if (!getEntityValueFromARef('PossessiveTargetAttribute', formData)) {
                return null;
            }
            // Show placeholder until tier2 for the target type has been retrieved (options only from tier2)
            const hasLoadedOptionsForCurrentTarget = !actorsLoading && tier2PossessiveTargetAttributeEntitlement != null;
            const shouldShowPlaceholder = actorsLoading || !hasLoadedOptionsForCurrentTarget;

            if (shouldShowPlaceholder) {
                let placeholderText = '';
                if (actorsLoading) {
                    placeholderText = currentValueDisplay ? `Loading... (${currentValueDisplay})` : 'Loading reference attributes...';
                } else {
                    placeholderText = currentValueDisplay || 'Waiting for options to load...';
                }

                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={isRequired} readOnly={isFieldDisabled} />
                        </Text>
                        <Box
                            style={{
                                minHeight: 56,
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid var(--mantine-color-default-border)',
                                borderRadius: 4,
                                padding: '6px 12px',
                                backgroundColor: 'var(--mantine-color-default-hover)',
                                color: 'var(--mantine-color-dimmed)'
                            }}
                        >
                            {actorsLoading && (
                                <Loader size="sm" mr="sm" />
                            )}
                            <Text size="sm" flex={1}>
                                {currentValueDisplay || placeholderText}
                            </Text>
                        </Box>
                        {selectedReferenceAttributeInfo ? (
                            <Text size="xs" c="dimmed" mt="xs" display="block">
                                Reference attributes from entity types referenced by {targetAttrName}
                            </Text>
                        ) : null}
                    </Box>
                ), 'PossessiveActorsAttributeLoading');
            }

            const possessiveActorsVal = getEntityValueFromARef('PossessiveActorsAttribute', formData);
            let selectedValue = Array.isArray(possessiveActorsVal)
                ? (possessiveActorsVal.length > 0 ? possessiveActorsVal[0] : '')
                : (possessiveActorsVal || '');

            const availableOptionIds = actorsOptions.map((opt) =>
                opt.id || opt.value || ''
            ).filter(Boolean);

            let valueIsValid = false;
            if (selectedValue && availableOptionIds.length > 0) {
                if (availableOptionIds.includes(selectedValue)) {
                    valueIsValid = true;
                } else {
                    console.warn(`⚠️ GenericEntityUpsertDialog: PossessiveActorsAttribute value "${selectedValue}" not found in available options. Attempting to match...`);
                    const matchingAttr = actorsOptions.find((opt) => {
                        const attrId = (opt.id || opt.value || '').toString().trim();
                        const attrName = (opt.name || opt.label || '').toString().trim();
                        const sv = (selectedValue ?? '').toString().trim();
                        return attrId === sv || attrId.toLowerCase() === sv.toLowerCase() ||
                            attrName === sv || attrName.toLowerCase() === sv.toLowerCase();
                    });
                    if (matchingAttr) {
                        const matchedId = matchingAttr.id || matchingAttr.value || '';
                        selectedValue = matchedId;
                        valueIsValid = true;
                        if (getEntityValueFromARef('PossessiveActorsAttribute', formData) !== matchedId) {
                            handleFieldChange(name, matchedId);
                        }
                    } else {
                        console.warn(`⚠️ GenericEntityUpsertDialog: Could not match PossessiveActorsAttribute "${selectedValue}" to any available option. Adding as disabled option.`);
                        valueIsValid = false;
                    }
                }
            }

            const handleActorsAttributeBrowserConfirm = (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
                if (selectedEntities.length > 0) {
                    const selectedId = selectedEntities[0].Id;
                    const selectedOpt = actorsOptions.find((opt) => {
                        const attrId = (opt.id || opt.value || '').toString().trim();
                        const sid = (selectedId ?? '').toString().trim();
                        return attrId === sid || attrId.toLowerCase() === sid.toLowerCase();
                    });
                    if (selectedOpt) {
                        const attrId = selectedOpt.id || selectedOpt.value || '';
                        handleFieldChange(name, attrId);
                    }
                }
                setActorsAttributeBrowserOpen(false);
            };

            const actorsBrowserEntities = actorsOptions.map((opt) => {
                const attrId = opt.id || opt.value || '';
                const attrLabel = opt.label || opt.name || attrId;
                return {
                    Id: attrId,
                    DisplayName: attrLabel,
                    Name: attrLabel,
                    Type: 'SchemaAttribute'
                };
            });

            const selectData = [
                { value: '', label: 'Select a reference attribute...' },
                ...(selectedValue && !valueIsValid ? [{ value: selectedValue, label: `${selectedValue} (not available)`, disabled: true }] : []),
                ...actorsOptions.map((opt) => {
                    const attrId = opt.id || opt.value || '';
                    const attrLabel = opt.label || opt.name || attrId;
                    return { value: attrId, label: attrLabel };
                })
            ];
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <Group align="flex-start" gap="xs">
                        <Select
                            label={label}
                            required={isRequired}
                            value={selectedValue}
                            onChange={(v) => handleFieldChange(name, v ?? '')}
                            disabled={isFieldDisabled}
                            data={selectData}
                            flex={1}
                        />
                        <ActionIcon
                            variant="subtle"
                            onClick={() => setActorsAttributeBrowserOpen(true)}
                            disabled={isFieldDisabled || actorsLoading || actorsOptions.length === 0 || !getEntityValueFromARef('PossessiveTargetAttribute', formData)}
                            mt="xs"
                            title="Search attributes"
                        >
                            {actorsLoading ? <Loader size="sm" /> : <IconSearch size={18} />}
                        </ActionIcon>
                    </Group>
                    {selectedValue && !valueIsValid && (
                        <Text size="xs" c="yellow" mt="xs" display="block">
                            Warning: The selected value "{selectedValue}" is not available in the current options. Please select a different value.
                        </Text>
                    )}
                    {getEntityValueFromARef('PossessiveTargetAttribute', formData) && selectedReferenceAttributeInfo ? (
                        <Text size="xs" c="dimmed" mt="xs" display="block">
                            Reference attributes from entity types referenced by {targetAttrName}
                        </Text>
                    ) : null}
                    <EntityBrowserDialog
                        open={actorsAttributeBrowserOpen}
                        onClose={() => setActorsAttributeBrowserOpen(false)}
                        onConfirm={handleActorsAttributeBrowserConfirm}
                        entityType="SchemaAttribute"
                        title="Select Reference Attribute"
                        callerName="GenericEntityUpsertDialog (PossessiveActorsAttribute)"
                        preloadedEntities={actorsBrowserEntities}
                        singleSelection={true}
                    />
                </Box>
            ), 'PossessiveActorsAttribute');
        }

        // Special handling for RequestApprovalType Type field (uses extracted renderer; tier3 AllowedValues when available). When Type changes to non-Possessive (e.g. Generic), clear possessive* fields; confirm first if any have data.
        if (isRequestApprovalType && isARef(attrRef, 'Type')) {
            const handleTypeChange = (fieldName: string, newValue: unknown) => {
                const nextType = (newValue ?? '').toString().trim().toLowerCase();
                if (nextType !== 'possessive') {
                    const scopeVal = (getEntityValueFromARef('PossessiveScope', formData) ?? '').toString().trim();
                    const targetVal = (getEntityValueFromARef('PossessiveTargetAttribute', formData) ?? '').toString().trim();
                    const actorsVal = (getEntityValueFromARef('PossessiveActorsAttribute', formData) ?? '').toString().trim();
                    const actorsPluralVal = (getEntityValueFromARef('PossessiveActorsAttributes', formData) ?? '').toString().trim();
                    const hasPossessiveData = !!(scopeVal || targetVal || actorsVal || actorsPluralVal);
                    if (hasPossessiveData) {
                        setPossessiveWipeConfirmPending((newValue ?? '').toString());
                        setPossessiveWipeConfirmOpen(true);
                        return;
                    }
                    handleFieldChange('PossessiveScope', '');
                    handleFieldChange('PossessiveTargetAttribute', '');
                    handleFieldChange('PossessiveActorsAttribute', '');
                    handleFieldChange('PossessiveActorsAttributes', '');
                }
                handleFieldChange(fieldName, newValue);
            };
            return wrapWithFieldDebug(name, renderTypeField({
                name,
                label,
                value,
                loading: isFieldDisabled,
                handleFieldChange: handleTypeChange,
                allowedValues: allowedValues?.length
                    ? (allowedValues as NonNullable<TypeFieldProps['allowedValues']>)
                    : undefined,
                required: isRequired
            }), 'RequestApprovalTypeType');
        }

        // Special handling for RequestApprovalType email template fields (email template selector; uses centralized list from dialog)
        if (isRequestApprovalType && (
            isARef(attrRef, 'CompletedApprovalEmail') || isARef(attrRef, 'PendingApprovalEmail') ||
            isARef(attrRef, 'RejectedRequestEmail') || isARef(attrRef, 'ExpiredEmail')
        )) {
            const emailTemplateFieldProps: CompletedApprovalEmailFieldProps = {
                name,
                label,
                value,
                isRequired,
                loading: isFieldDisabled,
                handleFieldChange,
                emailTemplates: emailTemplatesList,
                loadingTemplates: loadingEmailTemplates
            };
            return wrapWithFieldDebug(name, renderCompletedApprovalEmailField(emailTemplateFieldProps), 'CompletedApprovalEmail');
        }

        const isPossessiveScopeField = isARef(attrRef, 'PossessiveScope');

        // Special handling for PSGroup PossessiveScope field (RequestApprovalType and PluginFunction; entity type selector from tier1)
        // Required when Type is "Possessive". When Type is Generic, show disabled empty field (no selector).
        if (isRequestApprovalType && isPossessiveScopeField) {
            if (!isPossessiveType) {
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={false} readOnly={true} />
                        </Text>
                        <TextInput disabled value="" placeholder="—" />
                    </Box>
                ), 'PossessiveScope');
            }
            const handlePossessiveScopeChange = (fieldName: string, newValue: unknown) => {
                const prevScope = (getEntityValueFromARef('PossessiveScope', formData) ?? '')?.toString().trim();
                const nextScope = (newValue ?? '')?.toString().trim();
                if (prevScope !== nextScope) {
                    handleFieldChange('PossessiveTargetAttribute', '');
                    handleFieldChange('PossessiveActorsAttribute', '');
                }
                handleFieldChange(fieldName, newValue);
            };
            return wrapWithFieldDebug(name, renderPossessiveScopeField({ name, label, value, isRequired, loading: isFieldDisabled, formData, handleFieldChange: handlePossessiveScopeChange, entityTypes: allEntityTypes?.length ? entityTypesFromContext ?? undefined : undefined, excludeTypes: disjointedEntityTypes }), 'PossessiveScope');
        }

        // Special handling for PluginFunction PossessiveScope field (entity type selector from tier1)
        if (isPluginFunction && isPossessiveScopeField) {
            const handlePossessiveScopeChange = (fieldName: string, newValue: unknown) => {
                const prevScope = (getEntityValueFromARef('PossessiveScope', formData) ?? '')?.toString().trim();
                const nextScope = (newValue ?? '')?.toString().trim();
                if (prevScope !== nextScope) {
                    handleFieldChange('PossessiveTargetAttribute', '');
                    handleFieldChange('PossessiveActorsAttributes', '');
                }
                handleFieldChange(fieldName, newValue);
            };
            return wrapWithFieldDebug(name, renderPossessiveScopeField({ name, label, value, isRequired, loading: isFieldDisabled, formData, handleFieldChange: handlePossessiveScopeChange, entityTypes: allEntityTypes?.length ? entityTypesFromContext ?? undefined : undefined, excludeTypes: disjointedEntityTypes }), 'PossessiveScope');
        }

        // Special handling for PSGroup (RequestApprovalType) PossessiveActorsAttributes (plural: multiselect, tier2 reference attrs; backend uses string[])
        // When Type is Generic, show disabled empty field (no multiselect).
        if (isRequestApprovalType && isARef(attrRef, 'PossessiveActorsAttributes')) {
            if (!isPossessiveType) {
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={false} readOnly={true} />
                        </Text>
                        <TextInput disabled value="" placeholder="—" />
                    </Box>
                ), 'PossessiveActorsAttributes');
            }
            const ratActorsValue = formData[formKeyFromAttrName(name)] ?? formData[name];
            const ratActorsValueStr = Array.isArray(ratActorsValue)
                ? ratActorsValue.map((v: any) => (typeof v === 'string' ? v.trim() : (v?.Id ?? v?.id ?? v?.DisplayName ?? v?.displayName ?? '') || '')).filter(Boolean).join(', ')
                : (typeof ratActorsValue === 'string' ? ratActorsValue : (ratActorsValue ?? value ?? ''));
            const ratActorsTier2Options = referenceAttributesFromPossessiveTargetAttribute.map((a) => ({ name: a.name || a.id || '', label: a.label || a.name || a.id || '' })).filter((a) => a.name);
            return wrapWithFieldDebug(name, renderPossessiveActorsAttributesField({
                name,
                label,
                value: ratActorsValueStr,
                isRequired,
                loading: isFieldDisabled,
                formData,
                handleFieldChange,
                attributesFromTier2: ratActorsTier2Options,
                loadingFromTier2: loadingPossessiveTargetAttributeTier2
            }), 'PossessiveActorsAttributes');
        }

        // Special handling for PluginFunction PossessiveActorsAttributes field (options from tier2 for PossessiveTargetAttribute target type when selected)
        if (isPluginFunction && isARef(attrRef, 'PossessiveActorsAttributes')) {
            const pfActorsValue = formData[formKeyFromAttrName(name)] ?? formData[name];
            const pfActorsValueStr = Array.isArray(pfActorsValue)
                ? pfActorsValue.map((v: any) => (typeof v === 'string' ? v : (v?.Id ?? v?.id ?? v?.DisplayName ?? v?.displayName ?? '') || '')).filter(Boolean).join(', ')
                : (pfActorsValue ?? value ?? '');
            return wrapWithFieldDebug(name, renderPossessiveActorsAttributesField({
                name,
                label,
                value: typeof pfActorsValueStr === 'string' ? pfActorsValueStr : (pfActorsValueStr ?? value ?? ''),
                isRequired,
                loading: isFieldDisabled,
                formData,
                handleFieldChange,
                attributesFromTier2: attributesFromPossessiveTargetAttributeTier2,
                loadingFromTier2: loadingPossessiveTargetAttributeTier2
            }), 'PossessiveActorsAttributes');
        }

        if (attr.type === 'boolean') {
            const booleanDebugNotes = stringifyDebugNotes({
                name,
                label,
                type: attr.type,
                value,
                isRequired,
                computed: attr.computed,
                mutability: attr.mutability,
                entityType: entityType,
                isRequestField: !!(name && (name.toLowerCase() === 'request' || name.toLowerCase().includes('request')))
            });

            const isAddOrRemoveValue = !!name && ['addvalue', 'removevalue'].includes(name.toLowerCase().replace(/\s+/g, ''));
            const disableAddRemove = isPolicyRule && isAddOrRemoveValue && policyRuleOnlySingleValueAttributesSelected;
            const control = (
                <Checkbox
                    key={name}
                    checked={Boolean(value)}
                    onChange={(e) => handleFieldChange(name, e.currentTarget.checked)}
                    disabled={isFieldDisabled || disableAddRemove}
                    label={label}
                    required={isRequired}
                    mb="md"
                    data-notes={booleanDebugNotes}
                    data-field={name}
                    data-renderer="Checkbox"
                    data-type="boolean"
                />
            );
            return wrapWithFieldDebug(name, disableAddRemove ? (
                <Tooltip key={name} label="Add Value and Remove Value only apply to multi-valued attributes. Select at least one multi-valued attribute to enable these permissions.">
                    <Box component="span" display="inline-block">
                        {control}
                    </Box>
                </Tooltip>
            ) : control, 'Boolean');
        }

        const refToTypes = attr.isReferenceTo && Array.isArray(attr.isReferenceTo) && attr.isReferenceTo.length > 0 ? attr.isReferenceTo : [];
        if (attr.multiValued && attr.isReference && refToTypes.length > 0) {
            const currentRefs = Array.isArray(value) ? value : (value ? [value] : []);
            const refItems = currentRefs.map((item: any) => {
                if (item == null) return { id: '', display: '', entityType: undefined as string | undefined };
                if (typeof item === 'string') return { id: item, display: item, entityType: undefined };
                const refEntityType = item?.EntityType ?? item?.entityType;
                return {
                    id: item.Id ?? item.id ?? '',
                    display: item.DisplayName ?? item.displayName ?? item.Id ?? item.id ?? '',
                    ...(refEntityType != null && refEntityType !== '' && { entityType: String(refEntityType).trim() })
                };
            }).filter((r: { id: string }) => r.id);
            const handleMultiRefConfirm = (first: Array<{ Id: string; DisplayName?: string; Name?: string; EntityType?: string }> | string[], _effectiveEntityType?: string, selectedEntities?: Array<{ Id: string; DisplayName?: string; Name?: string }>) => {
                const existingIds = new Set(refItems.map((r: { id: string }) => r.id));
                const isEntityArray = Array.isArray(first) && first.length > 0 && typeof first[0] === 'object' && first[0] !== null && 'Id' in first[0];
                const idsToAdd = isEntityArray
                    ? (first as Array<{ Id: string }>).map((e) => e.Id).filter((id) => !existingIds.has(id))
                    : (first as string[]).filter((id) => !existingIds.has(id));
                const entityList = isEntityArray ? (first as Array<{ Id: string; DisplayName?: string; EntityType?: string }>) : (selectedEntities ?? []);
                const entityMap = new Map(entityList.map((e) => [e.Id, e]));
                const nextRefs = [
                    ...refItems.map((r: { id: string; display: string; entityType?: string }) => ({
                        Id: r.id,
                        DisplayName: r.display || undefined,
                        ...(r.entityType && { EntityType: r.entityType })
                    })),
                    ...idsToAdd.map((id) => {
                        const entity = entityMap.get(id) as { DisplayName?: string; Name?: string; EntityType?: string } | undefined;
                        return { Id: id, DisplayName: entity?.DisplayName ?? entity?.Name ?? undefined, EntityType: entity?.EntityType ?? undefined };
                    })
                ];
                handleFieldChange(name, nextRefs);
                setMultiRefBrowserField(null);
            };
            const removeRef = (id: string) => {
                const next = refItems
                    .filter((r: { id: string }) => r.id !== id)
                    .map((r: { id: string; display: string; entityType?: string }) => ({
                        Id: r.id,
                        DisplayName: r.display || undefined,
                        ...(r.entityType && { EntityType: r.entityType })
                    }));
                handleFieldChange(name, next);
            };
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                        <FieldLabelWithRequired label={label} required={isRequired} readOnly={isFieldDisabled} />
                    </Text>
                    <Box
                        id={`multi-ref-${name}`}
                        component="div"
                        onClick={!isFieldDisabled ? () => setMultiRefBrowserField(name) : undefined}
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 8,
                            alignItems: 'center',
                            minHeight: 40,
                            padding: 8,
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 4,
                            cursor: isFieldDisabled ? undefined : 'pointer'
                        }}
                    >
                        {refItems.length === 0 && !isFieldDisabled && (
                            <Text size="sm" c="dimmed">
                                Click to select {refToTypes.length > 1 ? '— choose entity type in dialog' : refToTypes[0]}
                            </Text>
                        )}
                        {refItems.map((r: { id: string; display: string; entityType?: string }) => (
                            <Group key={r.id} gap={4} wrap="nowrap" onMouseDown={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                                <MultiReferenceEntityBadge
                                    entityId={r.id}
                                    displayName={r.display}
                                    entityType={r.entityType}
                                    fallbackEntityTypes={refToTypes}
                                    leftClickNavigation={isFieldDisabled ? 'sameTab' : 'newTab'}
                                />
                                {!isFieldDisabled && (
                                    <ActionIcon
                                        variant="subtle"
                                        size="xs"
                                        onClick={(e) => { e.stopPropagation(); removeRef(r.id); }}
                                        aria-label="Remove"
                                    >
                                        <IconX size={12} />
                                    </ActionIcon>
                                )}
                            </Group>
                        ))}
                        {!isFieldDisabled && refItems.length > 0 && (
                            <Button variant="outlined" size="xs" onClick={(e) => { e.stopPropagation(); setMultiRefBrowserField(name); }}>
                                Add
                            </Button>
                        )}
                    </Box>
                    {refToTypes.length > 0 && (
                        <Text size="xs" c="dimmed" display="block" mt="xs">
                            Select from: {refToTypes.join(', ')}
                        </Text>
                    )}
                </Box>
            ), 'ReferenceFieldMulti');
        }

        const isTagsField = isARef(attrRef, 'Tags');

        if (attr.multiValued || isTagsField) {
            const currentDisplayValue = Array.isArray(value)
                ? value.map((v) => typeof v === 'string' ? v : (typeof v === 'object' && v !== null ? ((v as { Id?: string; id?: string; DisplayName?: string; displayName?: string }).Id || (v as { id?: string }).id || (v as { DisplayName?: string }).DisplayName || (v as { displayName?: string }).displayName || String(v)) : String(v))).join(', ')
                : (value || '');

            const rawInput: string = multiValuedRawInputs[name] !== undefined ? multiValuedRawInputs[name] : (typeof currentDisplayValue === 'string' ? currentDisplayValue : String(currentDisplayValue ?? ''));

            const tagsLength = isTagsField && typeof rawInput === 'string' ? rawInput.length : 0;
            const tagsExceedsLimit = isTagsField && tagsLength > 448;
            const tagsLabel = isTagsField
                ? <Group gap={6} align="center" wrap="nowrap"><span>{label}</span><Text component="span" size="xs" c="dimmed">Comma-separated values</Text></Group>
                : label;
            const tagsDescription = isTagsField && tagsExceedsLimit
                ? `Warning: Tags exceed 448 character limit (${tagsLength} characters)`
                : (!isTagsField ? 'Comma-separated values' : undefined);

            return wrapWithFieldDebug(name, (
                <TextInput
                    key={name}
                    label={tagsLabel}
                    value={rawInput}
                    onChange={(e) => {
                        const newValue = e.currentTarget.value;
                        setMultiValuedRawInputs((prev: Record<string, string>) => ({ ...prev, [name]: newValue }));
                        const arrayValue = newValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                        handleFieldChange(name, arrayValue);
                    }}
                    onBlur={() => {
                        const currentRawInput = multiValuedRawInputs[name] || '';
                        const arrayValue = currentRawInput.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                        setMultiValuedRawInputs((prev: Record<string, string>) => ({ ...prev, [name]: arrayValue.join(', ') }));
                        handleFieldChange(name, arrayValue);
                    }}
                    disabled={isFieldDisabled}
                    required={isRequired}
                    description={tagsDescription}
                    error={tagsExceedsLimit}
                    mb="md"
                />
            ), 'Tags');
        }

        const isDateField = isARef(attrRef, 'Dob') || attr.type === 'date' || attr.type === 'datetime' || attr.type === 'datetimeoffset';
        if (isDateField) {
            const dateValue = (() => {
                const v = value;
                if (v == null || v === '') return '';
                const s = String(v).trim();
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
                const d = new Date(s);
                if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
                return '';
            })();
            return wrapWithFieldDebug(name, (
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <TextInput
                        key={name}
                        label={label}
                        type="date"
                        value={dateValue}
                        onChange={(e) => handleFieldChange(name, e.currentTarget.value || '')}
                        disabled={isFieldDisabled}
                        required={isRequired}
                        rightSection={<IconCalendar size={18} />}
                        mb="md"
                    />
                </Tooltip>
            ), 'Date');
        }

        if (attr.type === 'number' || attr.type === 'integer') {
            const numberDisplayStr = value != null && value !== '' ? String(value) : '';
            const regexViolationMsgNum =
                bindingRegex && numberDisplayStr.trim()
                    ? getBindingViolationMessage(numberDisplayStr, { RegEx: bindingRegex })
                    : null;
            const errorMsgNum = regexViolationMsgNum ? (regexViolationMsgNum || 'Value does not match required pattern.') : undefined;
            const errorNodeNum =
                errorMsgNum && bindingRegex
                    ? <Tooltip label={`Required pattern: ${bindingRegex}`}><span>{errorMsgNum}</span></Tooltip>
                    : errorMsgNum;
            return wrapWithFieldDebug(name, (
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <TextInput
                        key={name}
                        label={label}
                        type="number"
                        value={numberDisplayStr}
                        onChange={(e) => handleFieldChange(name, e.currentTarget.value ? Number(e.currentTarget.value) : '')}
                        disabled={isFieldDisabled}
                        required={isRequired}
                        error={errorNodeNum}
                        mb="md"
                    />
                </Tooltip>
            ), 'Number');
        }

        if ((isContent || isEmailTemplate) && isARef(attrRef, 'Body')) {
            const contentType = getBodyContentType();
            const isMarkdown = contentType === 'text/markdown' || contentType === 'markdown';
            const isHtml = isEmailTemplate || (contentType === 'text/html' || contentType === 'html');

            if (isEmailTemplate && isARef(attrRef, 'Body')) {
                const currentValue = typeof value === 'string' ? value : '';
                return wrapWithFieldDebug(name, (
                    <BodyHtmlEditor
                        fieldName={name}
                        label={label}
                        value={currentValue}
                        onChange={(v) => handleFieldChange(name, v)}
                        isRequired={isRequired}
                        disabled={isFieldDisabled}
                        originalValue={typeof originalData?.[name] === 'string' ? originalData[name] : undefined}
                        variant="emailTemplate"
                    />
                ), 'BodyHtmlEditor');
            }

            if (isMarkdown) {
                return wrapWithFieldDebug(name, (
                    <ShadcnContentEditor
                        key={name}
                        value={typeof value === 'string' ? value : ''}
                        onChange={(newValue) => handleFieldChange(name, newValue)}
                        label={label}
                        isRequired={isRequired}
                        loading={loading}
                        disabled={isFieldDisabled}
                        height="500px"
                        name={`markdown-${name}`}
                        contentBodyDeepLink={isContent}
                        onContentLinkClick={isContent ? onContentLinkClick : undefined}
                    />
                ), 'MarkdownEditor');
            }

            if (isHtml) {
                const currentValue = typeof value === 'string' ? value : '';
                return wrapWithFieldDebug(name, (
                    <BodyHtmlEditor
                        fieldName={name}
                        label={label}
                        value={currentValue}
                        onChange={(v) => handleFieldChange(name, v)}
                        isRequired={isRequired}
                        disabled={isFieldDisabled}
                        variant="content"
                        contentBodyDeepLink={isContent}
                    />
                ), 'BodyHtmlEditor');
            }

            const contentDisplay = typeof value === 'string' ? value : '';
            return wrapWithFieldDebug(name, (
                <Textarea
                    key={name}
                    label={label}
                    value={contentDisplay}
                    onChange={(e) => handleFieldChange(name, e.currentTarget.value)}
                    onBlur={(e) => {
                        const v = e.currentTarget.value.trim();
                        if (v !== contentDisplay) handleFieldChange(name, v);
                    }}
                    disabled={isFieldDisabled}
                    required={isRequired}
                    rows={10}
                    mb="md"
                />
            ), 'ContentTextarea');
        }

        if (isHtmlField(name, label)) {
            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <Text size="sm" fw={500} mb="xs">
                        <FieldLabelWithRequired label={label} required={isRequired} readOnly={isFieldDisabled} />
                    </Text>
                    <Paper withBorder>
                        <ScimAceEditor
                            mode="html"
                            value={typeof value === 'string' ? value : ''}
                            onChange={(newValue) => handleFieldChange(name, newValue)}
                            name={`html-editor-${name}`}
                            height="400px"
                            fontSize={14}
                            showPrintMargin={false}
                            readOnly={isFieldDisabled}
                        />
                    </Paper>
                </Box>
            ), 'BodyHtmlLabel');
        }

        const normalizedNameForRef = (name || '').toLowerCase().replace(/\s+/g, '');
        const isPossessiveTargetByAnyId = normalizedNameForRef === 'possessivetargetattribute'
            || (attr.id != null && attr.id !== '' && normalizeKey(String(attr.id)) === 'possessivetargetattribute');
        if ((isRequestApprovalType || isPluginFunction) && isPossessiveTargetByAnyId) {
            if (isRequestApprovalType && !isPossessiveType) {
                return wrapWithFieldDebug(name, (
                    <Box key={name} mb="md">
                        <Text component="label" size="sm" fw={isRequired ? 500 : 400} mb="xs" display="block">
                            <FieldLabelWithRequired label={label} required={false} readOnly={true} />
                        </Text>
                        <TextInput disabled value="" placeholder="—" />
                    </Box>
                ), 'PossessiveTargetAttribute');
            }
            const fieldName = (attr.name || attr.id || attr.value || name || '').toString();
            const usePossessiveScopeTier2 = true;
            return wrapWithFieldDebug(name, renderPossessiveTargetAttributeField({
                name: fieldName,
                label,
                value: value ?? formData[fieldName],
                isRequired,
                loading: isFieldDisabled,
                loadingAllReferenceAttributes: loadingPossessiveScopeTier2,
                allReferenceAttributes: referenceAttributesFromPossessiveScope,
                formData,
                handleFieldChange,
                targetAttributeBrowserOpen: attributeRefBrowserField === fieldName,
                setTargetAttributeBrowserOpen: (open) => setAttributeRefBrowserField(open ? fieldName : null),
                dependentFieldToClear: 'PossessiveActorsAttribute',
                helperTextWhenEmpty: !possessiveScope ? 'Select PossessiveScope first' : undefined
            }), 'PossessiveTargetAttribute');
        }

        if (!attr.multiValued && attr.isReference && !isSetTransition) {
            const refTo = attr.isReferenceTo && Array.isArray(attr.isReferenceTo) && attr.isReferenceTo.length > 0
                ? attr.isReferenceTo
                : (Array.isArray(attr.Entities) ? attr.Entities : Array.isArray(attr.entities) ? attr.entities : []);
            const entityTypeNames = refTo.map((ent: any) =>
                typeof ent === 'string' ? ent : (ent?.DisplayName ?? ent?.Id ?? ent)
            ).filter(Boolean);

            const displayName = getReferenceDisplayName(formData ?? {}, name);

            const managedTypeForRef = (getEntityValueFromARef('ManagedType', formData) ?? existingEntity?.ManagedType ?? '').toString();
            const isExternalManagedForRef = entityType?.toLowerCase() === 'group' && managedTypeForRef?.toLowerCase() === 'external';

            const refField = (
                <GenericReferenceField
                    key={name}
                    name={name}
                    label={label}
                    value={value as string | EntityRef | null | undefined}
                    isRequired={isRequired}
                    loading={isFieldDisabled || isExternalManagedForRef}
                    entityTypes={entityTypeNames}
                    displayName={displayName}
                    onFieldChange={handleFieldChange}
                    onReferenceEntityUpdate={updateReferenceEntity}
                    onReferenceEntityClear={clearReferenceEntity}
                    entityService={entityService}
                />
            );
            return wrapWithFieldDebug(name, isReadOnlyField ? (
                <Tooltip label="This field cannot be edited">
                    <Box component="span" display="block">{refField}</Box>
                </Tooltip>
            ) : refField, 'GenericReferenceField');
        }

        if (isEmailTemplate && isARef(attrRef, 'ContentType')) {
            const contentTypeValue = typeof value === 'string' ? value.toLowerCase().trim() : '';
            const isNotHtml = contentTypeValue && contentTypeValue !== 'text/html' && contentTypeValue !== 'html';

            return wrapWithFieldDebug(name, (
                <TextInput
                    key={name}
                    label={label}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => handleFieldChange(name, e.currentTarget.value)}
                    disabled={isFieldDisabled}
                    required={isRequired}
                    error={!!isNotHtml}
                    description={isNotHtml ? 'Warning: Email templates should use "text/html" as ContentType. Other values may cause rendering issues.' : 'Email templates should use "text/html" as ContentType.'}
                    rightSection={isNotHtml ? (
                        <Tooltip label="Warning: ContentType should be 'text/html' for email templates">
                            <IconAlertTriangle size={20} color="var(--mantine-color-yellow-6)" />
                        </Tooltip>
                    ) : undefined}
                    mb="md"
                />
            ), 'ContentType');
        }

        const isBinaryType = attr.type && String(attr.type).toLowerCase() === 'binary';
        if (isBinaryType) {
            return wrapWithFieldDebug(name, (
                <ShadcnBinaryAttributeField
                    name={name}
                    label={label}
                    value={value}
                    loading={loading}
                    isViewSurface={isViewSurface}
                    readOnly={!!attr.readOnly || isFieldDisabled}
                    isEditMode={isEditMode}
                    isFile={isFile}
                    schemaAttributes={schemaAttributes as any}
                    targetEntitlement={targetEntitlement ?? null}
                    onFieldChange={(fieldName, v) => handleFieldChange(fieldName, v)}
                    onError={setError}
                />
            ), 'Binary');
        }

        if (isFile && isARef(attrRef, 'Data')) {
            const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (!file) {
                    debugLog('📁 File upload: No file selected');
                    return;
                }

                debugLog('📁 File upload: Starting upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);

                try {
                    const fileName = file.name;
                    const lastDotIndex = fileName.lastIndexOf('.');
                    const fileExtension = lastDotIndex >= 0 ? fileName.substring(lastDotIndex) : '';

                    let contentType = '';
                    try {
                        const matchingContentTypes = await contentTypesCacheService.getContentTypesByExtension(fileExtension);
                        if (matchingContentTypes.length > 0) {
                            contentType = matchingContentTypes[0].ContentType;
                        }
                    } catch (cacheError) {
                        console.error('📁 File upload: Failed to lookup content type from cache:', cacheError);
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const result = e.target?.result;
                        if (typeof result === 'string') {
                            const base64Data = result.includes(',')
                                ? result.split(',')[1]
                                : result;

                            handleFieldChangeRef.current(name, base64Data);

                            const fileNameField = schemaAttributes.find((schemaAttr: any) => {
                                const fieldNameLower = (schemaAttr.name || schemaAttr.id || schemaAttr.value || '').toLowerCase();
                                return fieldNameLower === 'filename';
                            });
                            if (fileNameField) {
                                const fileNameFieldName = fileNameField.name || fileNameField.id || fileNameField.value;
                                handleFieldChangeRef.current(fileNameFieldName as string, fileName);
                            }

                            if (contentType) {
                                const contentTypeField = schemaAttributes.find((schemaAttr: any) => {
                                    const fieldNameLower = (schemaAttr.name || schemaAttr.id || schemaAttr.value || '').toLowerCase();
                                    return fieldNameLower === 'contenttype';
                                });
                                if (contentTypeField) {
                                    const contentTypeFieldName = contentTypeField.name || contentTypeField.id || contentTypeField.value;
                                    handleFieldChangeRef.current(contentTypeFieldName as string, contentType);
                                }
                            }
                        } else {
                            console.error('📁 File upload: FileReader result is not a string:', typeof result);
                        }
                    };
                    reader.onerror = () => {
                        setError('Failed to read file');
                    };
                    reader.readAsDataURL(file);
                } catch (err) {
                    console.error('📁 File upload: Exception during file processing:', err);
                    setError(err instanceof Error ? err.message : 'Failed to process file');
                }
            };

            const hasExistingData = value && typeof value === 'string' && value.length > 0;
            const fileSizeInfo = hasExistingData
                ? ` (${Math.round(value.length * 0.75 / 1024)} KB base64 encoded)`
                : '';

            const currentFileName = (getEntityValueFromARef('FileName', formData) ?? (formData['File Name'] ?? '')).toString().trim() || '';
            const currentContentType = (getEntityValueFromARef('ContentType', formData) ?? '').toString().trim() || '';

            const fileDisplayValue = hasExistingData
                ? (currentFileName ? `✓ ${currentFileName}${fileSizeInfo}` : `✓ File uploaded${fileSizeInfo}`)
                : (currentFileName || 'No file chosen');
            const handleClearFile = () => {
                handleFieldChange(name, '');
                if (currentFileName) {
                    const fileNameField = schemaAttributes.find((schemaAttr: any) => {
                        const fieldNameLower = (schemaAttr.name || schemaAttr.id || schemaAttr.value || '').toLowerCase();
                        return fieldNameLower === 'filename';
                    });
                    if (fileNameField) {
                        const fileNameFieldName = fileNameField.name || fileNameField.id || fileNameField.value;
                        handleFieldChange(fileNameFieldName as string, '');
                    }
                }
                if (currentContentType) {
                    const contentTypeField = schemaAttributes.find((schemaAttr: any) => {
                        const fieldNameLower = (schemaAttr.name || schemaAttr.id || schemaAttr.value || '').toLowerCase();
                        return fieldNameLower === 'contenttype';
                    });
                    if (contentTypeField) {
                        const contentTypeFieldName = contentTypeField.name || contentTypeField.id || contentTypeField.value;
                        handleFieldChange(contentTypeFieldName as string, '');
                    }
                }
            };

            return wrapWithFieldDebug(name, (
                <TextInput
                    key={name}
                    label={label}
                    value={fileDisplayValue}
                    readOnly
                    rightSection={
                        <Group gap="xs">
                            <Button
                                variant="outlined"
                                size="xs"
                                component="label"
                                disabled={isFieldDisabled}
                                leftSection={<IconUpload size={16} />}
                            >
                                Upload
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    disabled={isFieldDisabled}
                                />
                            </Button>
                            {currentContentType ? (
                                <Badge variant="light" size="sm">{String(currentContentType)}</Badge>
                            ) : null}
                            {hasExistingData ? (
                                <Button
                                    variant="outlined"
                                    size="xs"
                                    color="red"
                                    onClick={handleClearFile}
                                    disabled={isFieldDisabled}
                                >
                                    Clear
                                </Button>
                            ) : null}
                        </Group>
                    }
                    description={hasExistingData && value && typeof value === 'string' && value.length > 100
                        ? `${(value.length * 0.75 / 1024).toFixed(0)} KB base64`
                        : undefined}
                    mb="md"
                />
            ), 'FileData');
        }

        if (isContent && isARef(attrRef, 'ContentType')) {
            const placeholderText = 'e.g., text/markdown, text/html (others treated as plain text)';
            const titleText = 'Special formats: text/markdown (Markdown editor), text/html (HTML editor). All other formats will be treated as plain text.';

            return wrapWithFieldDebug(name, (
                <TextInput
                    key={name}
                    label={label}
                    title={titleText}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => handleFieldChange(name, e.currentTarget.value)}
                    disabled={isFieldDisabled}
                    required={isRequired}
                    placeholder={placeholderText}
                    rightSection={
                        <Group gap="xs" wrap="nowrap">
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => handleFieldChange(name, 'text/markdown')}
                                disabled={isFieldDisabled}
                                px="sm"
                                style={{ minWidth: 'auto' }}
                            >
                                Md
                            </Button>
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={() => handleFieldChange(name, 'text/html')}
                                disabled={isFieldDisabled}
                                px="sm"
                                style={{ minWidth: 'auto' }}
                            >
                                Html
                            </Button>
                        </Group>
                    }
                    rightSectionWidth={110}
                    rightSectionPointerEvents="auto"
                    mb="md"
                />
            ), 'ContentType');
        }

        if (isFile && isARef(attrRef, 'ContentType')) {
            const contentTypeValue = typeof value === 'string' ? value.toLowerCase().trim() : '';
            const isTextContentType = contentTypeValue === 'text/css' ||
                contentTypeValue === 'text/html' ||
                contentTypeValue === 'text/markdown' ||
                contentTypeValue === 'text/yaml' ||
                contentTypeValue === 'application/x-yaml' ||
                contentTypeValue === 'application/yaml' ||
                contentTypeValue === 'css' ||
                contentTypeValue === 'html' ||
                contentTypeValue === 'markdown' ||
                contentTypeValue === 'yaml' ||
                contentTypeValue === 'yml';

            return wrapWithFieldDebug(name, (
                <Box key={name} mb="md">
                    <TextInput
                        label={label}
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => handleFieldChange(name, e.currentTarget.value)}
                        disabled={isFieldDisabled}
                        required={isRequired}
                        error={isTextContentType}
                        description={isTextContentType
                            ? 'Warning: Text content types (CSS, HTML, Markdown, YAML) are intended for Content entities, not File entities. Consider using a Content entity instead.'
                            : 'MIME type for the file (e.g., image/png, application/pdf)'}
                        rightSection={isTextContentType ? (
                            <Tooltip label="Warning: Text content types should use Content entities instead of File entities">
                                <IconAlertTriangle size={20} color="var(--mantine-color-yellow-6)" />
                            </Tooltip>
                        ) : undefined}
                    />
                    {isTextContentType && (
                        <Alert color="yellow" mt="xs">
                            <Text size="sm">
                                <strong>Note:</strong> Text content types (CSS, HTML, Markdown, YAML) are intended for <strong>Content entities</strong>, not File entities.
                                File entities are designed for binary files (images, PDFs, etc.). Consider creating a Content entity instead for text-based content.
                            </Text>
                        </Alert>
                    )}
                </Box>
            ), 'FileContentType');
        }

        const displayValue = typeof value === 'object' && value !== null
            ? (extractReferenceDisplayName(value) || extractReferenceId(value) || String(value))
            : (value || '');

        const isAccountNameRequired = isGroup && !isEditMode && isARef(attrRef, 'AccountName');
        const fieldRequired = isRequired || isAccountNameRequired;

        const hasNewlines = typeof displayValue === 'string' && (displayValue.includes('\n') || displayValue.includes('\r'));
        const isExpectedMultilineFieldResult = isExpectedMultilineField(name, label);
        const shouldBeMultiline = attr.type === 'text' && (label.toLowerCase().includes('description') || label.toLowerCase().includes('script')) || hasNewlines;
        const displayStr = typeof displayValue === 'string' ? displayValue : String(displayValue ?? '');
        const issues = hasWhitespaceIssues(displayStr) ? getWhitespaceIssues(displayStr) : [];
        const shouldShowWarning = issues.length > 0 && !isExpectedMultilineFieldResult;

        const fieldLabel = <FieldLabelWithRequired label={label} required={fieldRequired} readOnly={isReadOnlyField} />;

        if (allowedValues?.length && !attr.isReference) {
            const options = allowedValues.map((v: any) => {
                if (typeof v === 'string') return { value: v, label: v };
                return {
                    value: v?.value ?? v?.Id ?? v?.DisplayName ?? String(v),
                    label: v?.label ?? v?.DisplayName ?? v?.value ?? String(v)
                };
            });
            const uniqueOptionValuesLower = new Set(options.map((opt) => String(opt.value ?? '').toLowerCase()));
            const notAllAllowedValuesListed = uniqueOptionValuesLower.size < allowedValues.length;
            const allowedSetLower = uniqueOptionValuesLower;
            const currentValueStr = String(displayValue ?? '').trim();
            const currentNorm = currentValueStr.toLowerCase();
            const isValueAllowed = allowedSetLower.has(currentNorm);
            const showAllowedValuesWarning = !isValueAllowed && currentValueStr !== '';
            const regexViolationMsg = bindingRegex && currentValueStr ? getBindingViolationMessage(String(displayValue ?? ''), { RegEx: bindingRegex }) : null;
            const showRegexWarning = !!regexViolationMsg;

            const selectData = [
                { value: '', label: '(none)' },
                ...options.map((opt) => ({ value: String(opt.value), label: opt.label }))
            ];
            const errorMsg = [showAllowedValuesWarning && 'Value must match one of the allowed values (case-insensitive).', showRegexWarning && (regexViolationMsg || 'Value does not match required pattern.')].filter(Boolean).join(' ');
            const errorNode = (showAllowedValuesWarning || showRegexWarning) ? (showRegexWarning && bindingRegex ? <Tooltip label={`Required pattern: ${bindingRegex}`}><span>{errorMsg}</span></Tooltip> : errorMsg) : undefined;
            return wrapWithFieldDebug(name, (
                <Tooltip label={isReadOnlyField ? 'This field cannot be edited' : ''}>
                    <Select
                        key={name}
                        label={notAllAllowedValuesListed ? <>{fieldLabel} (Not all allowed values are listed)</> : fieldLabel}
                        required={fieldRequired}
                        withAsterisk={false}
                        value={typeof displayValue === 'string' ? displayValue : String(displayValue ?? '')}
                        onChange={(v) => handleFieldChange(name, v ?? '')}
                        disabled={isFieldDisabled}
                        data={selectData}
                        error={errorNode}
                        mb="md"
                    />
                </Tooltip>
            ), 'AllowedValuesSelect');
        }

        const tier3Item = getTargetEntitlementItemForAttribute(targetEntitlement ?? null, name);
        const tier2Item = getTier2AttributeBindingForAttribute(tier2Entitlement as any, name);
        const isEncryptedFromTier3 = tier3AttributeEncrypted(tier3Item?.Attribute);
        const isEncryptedFromTier2 =
            tier2Item?.attribute?.Encrypted === true ||
            (tier2Item?.attribute as unknown as { encrypted?: boolean } | undefined)?.encrypted === true;
        const isEncryptedAttr = isEncryptedFromTier3 || isEncryptedFromTier2;

        const rawAttrId = (tier3Item?.Attribute?.Id ?? name ?? '').toString().trim();
        const attributePath = rawAttrId.startsWith('$.')
            ? rawAttrId
            : (rawAttrId ? `$.${rawAttrId}` : `$.${name}`);

        const tier3DecryptEntitled = tier3ItemDecryptAllowed(tier3Item);
        const showTier3DecryptButton = !!entityId && isEncryptedFromTier3;

        if (isEncryptedAttr) {
            const encryptedMode: 'view' | 'edit' | 'create' = isEditMode
                ? isViewSurface
                    ? 'view'
                    : 'edit'
                : 'create';
            const regexViolationMsgEnc =
                encryptedMode === 'create' && bindingRegex && String(displayValue ?? '').trim()
                    ? getBindingViolationMessage(displayValue, { RegEx: bindingRegex })
                    : null;
            const errorNodeEnc =
                regexViolationMsgEnc && bindingRegex ? (
                    <Tooltip label={`Required pattern: ${bindingRegex}`}>
                        <span>{regexViolationMsgEnc || 'Value does not match required pattern.'}</span>
                    </Tooltip>
                ) : regexViolationMsgEnc
                  ? regexViolationMsgEnc || 'Value does not match required pattern.'
                  : undefined;
            const descMsgEnc =
                encryptedMode === 'create' && !regexViolationMsgEnc && shouldShowWarning
                    ? `⚠ Warning: ${issues.join(', ')}`
                    : undefined;
            const encryptRendererKey =
                encryptedMode === 'view'
                    ? 'EncryptedViewSurface'
                    : encryptedMode === 'create'
                      ? 'EncryptedPasswordCreate'
                      : revealedEncryptedFieldNames.includes(name)
                        ? 'EncryptedRevealed'
                        : 'EncryptedMasked';

            return wrapWithFieldDebug(
                name,
                (
                    <EncryptedAttributeField
                        mode={encryptedMode}
                        name={name}
                        label={label}
                        displayValue={displayValue}
                        fieldLabel={fieldLabel}
                        attributePath={attributePath}
                        entityId={entityId}
                        attr={attr as any}
                        isFieldDisabled={isFieldDisabled}
                        fieldRequired={fieldRequired}
                        isReadOnlyField={isReadOnlyField}
                        tier3DecryptEntitled={tier3DecryptEntitled}
                        showTier3DecryptButton={showTier3DecryptButton}
                        formData={formData}
                        originalData={originalData}
                        onFieldChange={handleFieldChange}
                        setOriginalData={setOriginalData}
                        decryptReveal={decryptReveal}
                        setDecryptReveal={setDecryptReveal}
                        revealedEncryptedFieldNames={revealedEncryptedFieldNames}
                        setRevealedEncryptedFieldNames={setRevealedEncryptedFieldNames}
                        encryptedFieldsClearOnSave={encryptedFieldsClearOnSave}
                        setEncryptedFieldsClearOnSave={setEncryptedFieldsClearOnSave}
                        encryptedCreateVisible={encryptedCreateVisible}
                        setEncryptedCreateVisible={setEncryptedCreateVisible}
                        encryptedEditStoredVisible={encryptedEditStoredVisible}
                        setEncryptedEditStoredVisible={setEncryptedEditStoredVisible}
                        encryptedViewDecryptedByName={encryptedViewDecryptedByName}
                        setEncryptedViewDecryptedByName={setEncryptedViewDecryptedByName}
                        encryptedViewPlainVisible={encryptedViewPlainVisible}
                        setEncryptedViewPlainVisible={setEncryptedViewPlainVisible}
                        revealedDecryptedCacheRef={revealedDecryptedCacheRef}
                        bindingRegex={bindingRegex}
                        bindingRegexError={errorNodeEnc}
                        bindingDescription={descMsgEnc}
                    />
                ),
                encryptRendererKey
            );
        }

        const regexViolationMsg = bindingRegex && String(displayValue ?? '').trim() ? getBindingViolationMessage(displayValue, { RegEx: bindingRegex }) : null;
        const showRegexWarning = !!regexViolationMsg;

        const fieldTooltipTitle = isReadOnlyField ? 'This field cannot be edited' : getFieldTooltipTitle(attr);
        const errorMsg = showRegexWarning ? (regexViolationMsg || 'Value does not match required pattern.') : undefined;
        const errorNode = errorMsg && bindingRegex ? <Tooltip label={`Required pattern: ${bindingRegex}`}><span>{errorMsg}</span></Tooltip> : errorMsg;
        const descMsg = !showRegexWarning && (shouldShowWarning ? `⚠ Warning: ${issues.join(', ')}` : isExternalManaged ? 'This group is managed by ActiveDirectory and cannot be edited' : undefined);
        const FieldComponent = shouldBeMultiline ? Textarea : TextInput;
        const rows = shouldBeMultiline ? (attr.type === 'text' && (label.toLowerCase().includes('description') || label.toLowerCase().includes('script')) ? 3 : 2) : undefined;
        const rawDisplay = typeof displayValue === 'string' || typeof displayValue === 'number' ? displayValue : String(displayValue ?? '');
        const currentDisplay = isFieldDisabled ? formatPhoneDisplayIfApplicable(label, rawDisplay) : rawDisplay;
        return wrapWithFieldDebug(name, (
            <Tooltip label={fieldTooltipTitle}>
                <FieldComponent
                    key={name}
                    label={fieldLabel}
                    value={currentDisplay}
                    onChange={(e) => handleFieldChange(name, e.currentTarget.value)}
                    onBlur={(e) => {
                        if (isFieldDisabled) return;
                        const trimmed = e.currentTarget.value.trim();
                        if (e.currentTarget.value !== trimmed) handleFieldChange(name, trimmed);
                    }}
                    disabled={isFieldDisabled}
                    required={fieldRequired}
                    withAsterisk={false}
                    rows={rows}
                    error={errorNode}
                    description={descMsg}
                    mb="md"
                />
            </Tooltip>
        ), 'DefaultText', isStubValueInner);
}
